import { axiosInstance } from '../utils/axiosinstance';

// Types for hackathon and resource assignment

export interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'upcoming' | 'completed' | string;
  photo?: string;
  isRegistrationOpen?: boolean;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  // Add more fields as needed
}

export interface HackathonsResponse {
  success: boolean;
  message?: string;
  data: Hackathon[];
}

export interface SingleHackathonResponse {
  success: boolean;
  message?: string;
  data: {
    title?: string;
    hackathon: Hackathon;
  };
}

export interface CreateHackathonRequest {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  photo?: string;
  // Add more fields as needed
}

export interface UpdateHackathonRequest {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  photo?: string;
  // Add more fields as needed
}

// Resource assignment types
export interface HackathonResource {
  _id: string;
  id: string;
  title: string;
  description: string;
  resourceType: string;
  link: string;
  categoryId: {
    _id: string;
    name: string;
  };
  assigned: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface HackathonResourcesResponse {
  success: boolean;
  message?: string;
  data: {
    hackathon: {
      id: string;
      title: string;
    };
    resources: HackathonResource[];
  };
}

export interface AssignResourcesRequest {
  resourceIds: string[];
}

export interface AssignResourcesResponse {
  success: boolean;
  message?: string;
  data: {
    assignedResources: HackathonResource[];
  };
}

export interface AvailableResourcesResponse {
  success: boolean;
  message?: string;
  data: {
    resources: (HackathonResource & { assigned: boolean })[];
  };
}

export interface UserHackathonResourcesResponse {
  success: boolean;
  message?: string;
  data: HackathonResource[];
}

// Upload types
export interface UploadResponse {
  success: boolean;
  message?: string;
  data: {
    url: string;
    filename: string;
    originalName: string;
    size: number;
    mimetype: string;
  };
}

export interface UploadRequest {
  image: File;
  folder?: string;
}

// Hackathon API functions
export const hackathonApi = {
  // Get all hackathons
  getHackathons: async (): Promise<HackathonsResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.get('/api/v1/hackathons', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Create new hackathon
  createHackathon: async (hackathonData: CreateHackathonRequest): Promise<SingleHackathonResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.post('/api/v1/hackathons', hackathonData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Get currently active hackathon
  getActiveHackathon: async (): Promise<SingleHackathonResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.get('/api/v1/hackathons/active', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Get upcoming hackathons
  getUpcomingHackathons: async (): Promise<HackathonsResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.get('/api/v1/hackathons/upcoming', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Get hackathon by ID
  getHackathonById: async (id: string): Promise<SingleHackathonResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.get(`/api/v1/hackathons/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Update hackathon
  updateHackathon: async (id: string, updateData: UpdateHackathonRequest): Promise<SingleHackathonResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.put(`/api/v1/hackathons/${id}`, updateData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Delete hackathon
  deleteHackathon: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.delete(`/api/v1/hackathons/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Get resources assigned to a specific hackathon
  getHackathonResources: async (
    hackathonId: string, 
    params?: { category?: string; type?: string }
  ): Promise<HackathonResourcesResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.get(`/api/v1/hackathons/${hackathonId}/resources`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        category: params?.category,
        type: params?.type,
      }
    });
    return response.data;
  },

  // Assign resources to hackathon
  assignResourcesToHackathon: async (
    hackathonId: string,
    resourceIds: string[]
  ): Promise<AssignResourcesResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.post(
      `/api/v1/hackathons/${hackathonId}/resources`,
      { resourceIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  },

  // Get all resources with assignment status for admin interface
  getAvailableResourcesForHackathon: async (
    hackathonId: string
  ): Promise<AvailableResourcesResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.get(
      `/api/v1/hackathons/${hackathonId}/resources/available`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  // Get resources for users (hackathon-specific + general)
  getUserHackathonResources: async (
    hackathonId: string
  ): Promise<UserHackathonResourcesResponse> => {
    const response = await axiosInstance.get(
      `/api/v1/hackathons/${hackathonId}/resources/user`
    );
    return response.data;
  },

  // Remove resource from hackathon
  removeResourceFromHackathon: async (
    hackathonId: string,
    resourceId: string
  ): Promise<{ success: boolean; message?: string }> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.delete(
      `/api/v1/hackathons/${hackathonId}/resources/${resourceId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  // Start hackathon
  startHackathon: async (id: string): Promise<SingleHackathonResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.post(`/api/v1/hackathons/${id}/start`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // End hackathon
  endHackathon: async (id: string): Promise<SingleHackathonResponse> => {
    const token = localStorage.getItem('adminToken');
    const response = await axiosInstance.post(`/api/v1/hackathons/${id}/end`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Upload file
  uploadFile: async (uploadData: UploadRequest): Promise<UploadResponse> => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', uploadData.image);
    if (uploadData.folder) {
      formData.append('folder', uploadData.folder);
    }
    
    const response = await axiosInstance.post('/api/v1/upload', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
